/**
 * Canvas2D renderer. Direct port of the Compose Canvas drawing routines in
 * `VerticalSnakeScreen.kt`. The world is in world coordinates; we render
 * to the canvas using a `scale` (world units → pixels) and `headOffsetY`
 * (the head stays at a fixed screen y).
 */
import type { Obstacle, World } from '../game/engine/types';
import { type Palette, glowBlurRadius, withAlpha } from './palette';
import type { ColorBlindMode, GlowLevel, ThemeChoice } from '../data/settings';

export interface RenderOptions {
  width: number;
  height: number;
  /** world units → screen pixels */
  scale: number;
  /** Screen-space y at which the snake head sits. */
  headOffsetY: number;
  theme: ThemeChoice;
  glow: GlowLevel;
  colorBlind: ColorBlindMode;
}

const TAU = Math.PI * 2;

function rgba(c: number, aMul = 1): string {
  const a = ((c >>> 24) & 0xff) / 255;
  const r = (c >>> 16) & 0xff;
  const g = (c >>> 8) & 0xff;
  const b = c & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${a * aMul})`;
}

function toScreen(
  worldX: number,
  worldY: number,
  scale: number,
  headOffsetY: number,
  worldScrollY: number,
): [number, number] {
  return [worldX * scale, (worldScrollY - worldY) * scale + headOffsetY];
}

function drawBackground(
  ctx: CanvasRenderingContext2D,
  world: World,
  palette: Palette,
  w: number,
  h: number,
): void {
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, rgba(palette.bgTop));
  grad.addColorStop(1, rgba(palette.bgBottom));
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // Parallax lane stripes for a sense of motion.
  ctx.save();
  ctx.globalAlpha = 0.18;
  const stripeY = ((world.scrollY * 0.15) % 240) - 240;
  ctx.fillStyle = rgba(palette.accent, 0.6);
  for (let y = stripeY; y < h; y += 240) {
    ctx.fillRect(0, y, w, 2);
    ctx.fillRect(0, y + 120, w, 1);
  }
  ctx.restore();
}

export function render(
  ctx: CanvasRenderingContext2D,
  world: World,
  options: RenderOptions,
  palette: Palette,
): void {
  const { width: w, height: h, scale, headOffsetY } = options;

  let shakeX = 0;
  let shakeY = 0;
  if (world.shakeSeconds > 0) {
    shakeX = (Math.random() - 0.5) * world.shakeMagnitude;
    shakeY = (Math.random() - 0.5) * world.shakeMagnitude;
  }

  ctx.save();
  ctx.translate(shakeX, shakeY);

  drawBackground(ctx, world, palette, w, h);

  // Apply a soft glow halo to the play layer (obstacles, food, snake,
  // particles, shield). The HUD overlays below should stay sharp.
  const blur = glowBlurRadius(options.glow, scale);
  if (blur > 0) {
    ctx.shadowBlur = blur;
    ctx.shadowColor = rgba(palette.accent, 0.7);
  }

  drawAllObstacles(ctx, world, palette, scale, headOffsetY, h);
  drawFood(ctx, world, palette, scale, headOffsetY, h / scale);
  drawSnake(ctx, world, palette, scale, headOffsetY, h / scale);
  drawAllParticles(ctx, world, scale, headOffsetY, h / scale);
  drawAllPopups(ctx, world, scale, headOffsetY, h / scale);
  drawSpeedLines(ctx, world, palette, w, h);
  drawInvincibilityAura(ctx, world, palette, scale, headOffsetY);

  // Disable glow before drawing the HUD overlays so they remain crisp.
  if (blur > 0) {
    ctx.shadowBlur = 0;
    ctx.shadowColor = 'transparent';
  }

  drawRushCalmOverlay(ctx, world, palette, w, h);
  drawBiomeBanner(ctx, world, palette, w, h);

  ctx.restore();
}

function drawAllObstacles(
  ctx: CanvasRenderingContext2D,
  world: World,
  p: Palette,
  scale: number,
  headOffsetY: number,
  canvasHeight: number,
): void {
  for (const o of world.obstacles) {
    const [sx, sy] = toScreen(o.x, o.y, scale, headOffsetY, world.scrollY);
    if (sy < -300 || sy > canvasHeight + 300) continue;
    drawObstacle(ctx, o, sx, sy, scale, p, world.scrollY);
  }
}

function drawObstacle(
  ctx: CanvasRenderingContext2D,
  o: Obstacle,
  sx: number,
  sy: number,
  scale: number,
  p: Palette,
  scrollY: number,
): void {
  switch (o.type) {
    case 'GATE':
      drawGate(ctx, o, sx, sy, scale, p);
      break;
    case 'PULSE_WALL':
      drawPulseWall(ctx, o, sx, sy, scale, p);
      break;
    case 'DOUBLE_GATE':
      drawDoubleGate(ctx, o, sx, sy, scale, p);
      break;
    case 'LOG':
      drawLog(ctx, o, sx, sy, scale, p);
      break;
    case 'BEEHIVE':
      drawBeehive(ctx, o, sx, sy, scale, p, scrollY);
      break;
    case 'KELP_CURTAIN':
      drawKelpCurtain(ctx, o, sy, scale, p);
      break;
    case 'JELLYFISH':
      drawJellyfish(ctx, o, sx, sy, scale, p, scrollY);
      break;
    case 'CORAL_SPIRE':
      drawCoralSpire(ctx, o, sx, sy, scale, p);
      break;
    case 'PORTAL_WALL':
      drawPortalWall(ctx, o, sy, scale, p);
      break;
    case 'DIAGONAL_BEAM':
      drawDiagonalBeam(ctx, o, sx, sy, scale, p);
      break;
    case 'CROSS_GATE':
      drawCrossGate(ctx, o, sx, sy, scale, p, scrollY);
      break;
    case 'SHIFTING_WALL':
      drawShiftingWall(ctx, o, sy, scale, p, scrollY);
      break;
    case 'CHAOS_RING':
      drawChaosRing(ctx, o, sy, scale, p, scrollY);
      break;
    case 'COMET':
      drawComet(ctx, o, sx, sy, scale);
      break;
  }
}

// ── Per-obstacle-type drawers ─────────────────────────────────

function drawGate(
  ctx: CanvasRenderingContext2D,
  o: Extract<Obstacle, { type: 'GATE' }>,
  _sx: number,
  sy: number,
  scale: number,
  p: Palette,
): void {
  const leftEnd = o.gapCenterX - o.gapWidth / 2;
  const rightStart = o.gapCenterX + o.gapWidth / 2;
  // Dark obstacle body — clearly reads as "wall".
  ctx.fillStyle = rgba(p.wallFill, 0.92);
  ctx.fillRect(0, sy - (o.pillarHeight * scale) / 2, leftEnd * scale, o.pillarHeight * scale);
  ctx.fillRect(
    rightStart * scale,
    sy - (o.pillarHeight * scale) / 2,
    720 * scale - rightStart * scale,
    o.pillarHeight * scale,
  );
  // Bright edge markers — the player's "this is the safe gap" cue.
  ctx.fillStyle = rgba(p.wallEdge, 0.95);
  ctx.fillRect(leftEnd * scale - 4, sy - (o.pillarHeight * scale) / 2, 8, o.pillarHeight * scale);
  ctx.fillRect(
    rightStart * scale - 4,
    sy - (o.pillarHeight * scale) / 2,
    8,
    o.pillarHeight * scale,
  );
}

function drawPulseWall(
  ctx: CanvasRenderingContext2D,
  o: Extract<Obstacle, { type: 'PULSE_WALL' }>,
  _sx: number,
  sy: number,
  scale: number,
  p: Palette,
): void {
  const gapEnd = o.gapStart + o.gapWidth;
  ctx.fillStyle = rgba(p.wallFill, 0.92);
  ctx.fillRect(0, sy - 24 * scale, o.gapStart * scale, 48 * scale);
  ctx.fillRect(gapEnd * scale, sy - 24 * scale, o.totalWidth * scale - gapEnd * scale, 48 * scale);
  ctx.fillStyle = rgba(p.wallEdge, 0.95);
  ctx.fillRect(o.gapStart * scale - 4, sy - 24 * scale, 8, 48 * scale);
  ctx.fillRect(gapEnd * scale - 4, sy - 24 * scale, 8, 48 * scale);
}

function drawDoubleGate(
  ctx: CanvasRenderingContext2D,
  o: Extract<Obstacle, { type: 'DOUBLE_GATE' }>,
  _sx: number,
  sy: number,
  scale: number,
  p: Palette,
): void {
  for (const sign of [-1, 1]) {
    const y = sy + (sign * o.spacing * scale) / 2;
    const leftEnd = o.gapCenterX - o.gapWidth / 2;
    const rightStart = o.gapCenterX + o.gapWidth / 2;
    ctx.fillStyle = rgba(p.wallFill, 0.92);
    ctx.fillRect(0, y - 24 * scale, leftEnd * scale, 48 * scale);
    ctx.fillRect(rightStart * scale, y - 24 * scale, 720 * scale - rightStart * scale, 48 * scale);
    ctx.fillStyle = rgba(p.wallEdge, 0.9);
    ctx.fillRect(leftEnd * scale - 4, y - 24 * scale, 8, 48 * scale);
    ctx.fillRect(rightStart * scale - 4, y - 24 * scale, 8, 48 * scale);
  }
}

function drawLog(
  ctx: CanvasRenderingContext2D,
  o: Extract<Obstacle, { type: 'LOG' }>,
  sx: number,
  sy: number,
  scale: number,
  _p: Palette,
): void {
  ctx.save();
  ctx.translate(sx, sy);
  const t = performance.now() / 1000;
  ctx.rotate(o.rotation + t * 0.5);
  ctx.fillStyle = '#8B4513';
  ctx.fillRect((-o.width * scale) / 2, (-o.height * scale) / 2, o.width * scale, o.height * scale);
  ctx.strokeStyle = '#40ffc0';
  ctx.lineWidth = 2 * scale;
  ctx.strokeRect(
    (-o.width * scale) / 2,
    (-o.height * scale) / 2,
    o.width * scale,
    o.height * scale,
  );
  ctx.restore();
}

function drawBeehive(
  ctx: CanvasRenderingContext2D,
  o: Extract<Obstacle, { type: 'BEEHIVE' }>,
  sx: number,
  sy: number,
  scale: number,
  p: Palette,
  scrollY: number,
): void {
  const phase = scrollY * 0.05 + o.phase;
  // Bees are danger — use danger color so they don't get confused with food.
  for (let i = 0; i < o.beeCount; i++) {
    const a = phase + (i / o.beeCount) * TAU;
    const bx = sx + Math.cos(a) * o.ringRadius * scale;
    const by = sy + Math.sin(a) * o.ringRadius * scale;
    ctx.fillStyle = rgba(p.danger, 1);
    ctx.beginPath();
    ctx.arc(bx, by, 6 * scale, 0, TAU);
    ctx.fill();
  }
  // Threat halo.
  ctx.fillStyle = rgba(p.danger, 0.18);
  ctx.beginPath();
  ctx.arc(sx, sy, o.ringRadius * scale * 0.6, 0, TAU);
  ctx.fill();
}

function drawKelpCurtain(
  ctx: CanvasRenderingContext2D,
  o: Extract<Obstacle, { type: 'KELP_CURTAIN' }>,
  sy: number,
  scale: number,
  p: Palette,
): void {
  ctx.fillStyle = rgba(p.wallFill, 0.98);
  let lastX = 0;
  for (const [gs, ge] of o.slits) {
    if (gs > lastX) {
      ctx.fillRect(lastX * scale, sy - 100 * scale, (gs - lastX) * scale, 200 * scale);
    }
    lastX = ge;
  }
  if (lastX < o.totalWidth) {
    ctx.fillRect(lastX * scale, sy - 100 * scale, (o.totalWidth - lastX) * scale, 200 * scale);
  }
  // Bright edge markers at slit boundaries — the player's "safe path" cue.
  ctx.fillStyle = rgba(p.wallEdge, 0.95);
  for (const [gs, ge] of o.slits) {
    ctx.fillRect(gs * scale - 4, sy - 100 * scale, 8, 200 * scale);
    ctx.fillRect(ge * scale - 4, sy - 100 * scale, 8, 200 * scale);
  }
}

function drawJellyfish(
  ctx: CanvasRenderingContext2D,
  o: Extract<Obstacle, { type: 'JELLYFISH' }>,
  sx: number,
  sy: number,
  scale: number,
  p: Palette,
  scrollY: number,
): void {
  const t = scrollY * 0.012 + o.driftPhase;
  const cx = sx + Math.sin(t) * o.driftAmplitude * scale;
  // Jellyfish are obstacles — use the danger color so the player reads
  // them as "don't touch". The pulsing outline comes from the wallEdge.
  ctx.fillStyle = rgba(p.danger, 0.85);
  ctx.beginPath();
  ctx.ellipse(cx, sy, 22 * scale, 14 * scale, 0, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(p.wallEdge, 0.7);
  ctx.lineWidth = 2 * scale;
  for (let i = -2; i <= 2; i++) {
    ctx.beginPath();
    ctx.moveTo(cx + i * 6 * scale, sy + 12 * scale);
    ctx.lineTo(cx + i * 6 * scale, sy + 28 * scale);
    ctx.stroke();
  }
}

function drawCoralSpire(
  ctx: CanvasRenderingContext2D,
  o: Extract<Obstacle, { type: 'CORAL_SPIRE' }>,
  sx: number,
  sy: number,
  scale: number,
  p: Palette,
): void {
  // Coral spires are tall pillars the player must steer around — danger.
  ctx.fillStyle = rgba(p.danger, 0.92);
  ctx.fillRect(
    sx - o.halfWidth * scale,
    sy - (o.height * scale) / 2,
    o.halfWidth * 2 * scale,
    o.height * scale,
  );
  // Bright outline so the pillar reads on every biome background.
  ctx.strokeStyle = rgba(p.wallEdge, 0.85);
  ctx.lineWidth = 2 * scale;
  ctx.strokeRect(
    sx - o.halfWidth * scale,
    sy - (o.height * scale) / 2,
    o.halfWidth * 2 * scale,
    o.height * scale,
  );
}

function drawPortalWall(
  ctx: CanvasRenderingContext2D,
  o: Extract<Obstacle, { type: 'PORTAL_WALL' }>,
  sy: number,
  scale: number,
  p: Palette,
): void {
  ctx.fillStyle = rgba(p.wallFill, 0.98);
  let lastX = 0;
  for (const [gs, ge] of o.gaps) {
    if (gs > lastX) {
      ctx.fillRect(lastX * scale, sy - 24 * scale, (gs - lastX) * scale, 48 * scale);
    }
    lastX = ge;
  }
  if (lastX < o.totalWidth) {
    ctx.fillRect(lastX * scale, sy - 24 * scale, (o.totalWidth - lastX) * scale, 48 * scale);
  }
  // Bright edge markers at gap boundaries — the player's "safe path" cue.
  ctx.fillStyle = rgba(p.wallEdge, 0.95);
  for (const [gs, ge] of o.gaps) {
    ctx.fillRect(gs * scale - 4, sy - 24 * scale, 8, 48 * scale);
    ctx.fillRect(ge * scale - 4, sy - 24 * scale, 8, 48 * scale);
  }
}

function drawDiagonalBeam(
  ctx: CanvasRenderingContext2D,
  o: Extract<Obstacle, { type: 'DIAGONAL_BEAM' }>,
  sx: number,
  sy: number,
  scale: number,
  p: Palette,
): void {
  ctx.save();
  ctx.translate(sx, sy);
  ctx.rotate(o.angle);
  // Diagonal beams are Lattice-bime obstacles — use danger color so they
  // read as "hostile diagonal slash".
  ctx.fillStyle = rgba(p.danger, 0.92);
  ctx.fillRect(
    (-o.length * scale) / 2,
    (-o.thickness * scale) / 2,
    o.length * scale,
    o.thickness * scale,
  );
  ctx.strokeStyle = rgba(p.wallEdge, 0.8);
  ctx.lineWidth = 2 * scale;
  ctx.strokeRect(
    (-o.length * scale) / 2,
    (-o.thickness * scale) / 2,
    o.length * scale,
    o.thickness * scale,
  );
  ctx.restore();
}

function drawCrossGate(
  ctx: CanvasRenderingContext2D,
  o: Extract<Obstacle, { type: 'CROSS_GATE' }>,
  sx: number,
  sy: number,
  scale: number,
  p: Palette,
  scrollY: number,
): void {
  const armAngle = ((scrollY * 0.001 * o.rotationSpeed + o.phase) * (Math.PI / 2)) % TAU;
  ctx.save();
  ctx.translate(sx, sy);
  ctx.rotate(armAngle);
  // Cross gates rotate — danger color + bright outline so the player
  // can see the arms sweep across the gap they're aiming for.
  ctx.fillStyle = rgba(p.danger, 0.9);
  for (let i = 0; i < 2; i++) {
    ctx.save();
    ctx.rotate((i * Math.PI) / 2);
    ctx.fillRect(
      -o.armLength * scale,
      (-o.armThickness * scale) / 2,
      o.armLength * 2 * scale,
      o.armThickness * scale,
    );
    ctx.restore();
  }
  ctx.strokeStyle = rgba(p.wallEdge, 0.7);
  ctx.lineWidth = 2 * scale;
  for (let i = 0; i < 2; i++) {
    ctx.save();
    ctx.rotate((i * Math.PI) / 2);
    ctx.strokeRect(
      -o.armLength * scale,
      (-o.armThickness * scale) / 2,
      o.armLength * 2 * scale,
      o.armThickness * scale,
    );
    ctx.restore();
  }
  ctx.restore();
}

function drawShiftingWall(
  ctx: CanvasRenderingContext2D,
  o: Extract<Obstacle, { type: 'SHIFTING_WALL' }>,
  sy: number,
  scale: number,
  p: Palette,
  scrollY: number,
): void {
  const t = scrollY * o.speed + o.phase;
  const center = o.totalWidth / 2 + Math.sin(t) * o.amplitude;
  const gs = Math.max(0, Math.min(center - o.gapWidth / 2, o.totalWidth - o.gapWidth));
  const ge = gs + o.gapWidth;
  ctx.fillStyle = rgba(p.wallFill, 0.98);
  ctx.fillRect(0, sy - 24 * scale, gs * scale, 48 * scale);
  ctx.fillRect(ge * scale, sy - 24 * scale, o.totalWidth * scale - ge * scale, 48 * scale);
  // Bright edge markers at gap boundaries — the player's "safe path" cue.
  ctx.fillStyle = rgba(p.wallEdge, 0.95);
  ctx.fillRect(gs * scale - 4, sy - 24 * scale, 8, 48 * scale);
  ctx.fillRect(ge * scale - 4, sy - 24 * scale, 8, 48 * scale);
}

function drawChaosRing(
  ctx: CanvasRenderingContext2D,
  o: Extract<Obstacle, { type: 'CHAOS_RING' }>,
  sy: number,
  scale: number,
  p: Palette,
  scrollY: number,
): void {
  for (const sign of [-1, 1]) {
    const y = sy + (sign * o.spacing * scale) / 2;
    const t = scrollY * o.slideSpeed + (sign > 0 ? o.outerPhase : o.innerPhase);
    const gw = sign > 0 ? o.outerGapWidth : o.innerGapWidth;
    const center = o.totalWidth / 2 + Math.sin(t) * (o.totalWidth - gw) * 0.35;
    const gs = Math.max(0, Math.min(center - gw / 2, o.totalWidth - gw));
    const ge = gs + gw;
    ctx.fillStyle = rgba(p.wallFill, 0.98);
    ctx.fillRect(0, y - 24 * scale, gs * scale, 48 * scale);
    ctx.fillRect(ge * scale, y - 24 * scale, o.totalWidth * scale - ge * scale, 48 * scale);
    // Bright edge markers at gap boundaries.
    ctx.fillStyle = rgba(p.wallEdge, 0.9);
    ctx.fillRect(gs * scale - 4, y - 24 * scale, 8, 48 * scale);
    ctx.fillRect(ge * scale - 4, y - 24 * scale, 8, 48 * scale);
  }
}

function drawComet(
  ctx: CanvasRenderingContext2D,
  o: Extract<Obstacle, { type: 'COMET' }>,
  sx: number,
  sy: number,
  scale: number,
): void {
  const bodyRadius = o.radiusBody * scale;
  ctx.fillStyle = '#ff4400';
  ctx.strokeStyle = '#ffcc00';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(sx, sy, bodyRadius, 0, TAU);
  ctx.fill();
  ctx.stroke();
  ctx.strokeStyle = '#ffaa00';
  ctx.lineWidth = 3 * scale;
  ctx.beginPath();
  ctx.moveTo(sx, sy);
  ctx.lineTo(sx - Math.sign(o.velocityX) * 60 * scale, sy);
  ctx.stroke();
}

function drawFood(
  ctx: CanvasRenderingContext2D,
  world: World,
  p: Palette,
  scale: number,
  headOffsetY: number,
  worldHeight: number,
): void {
  const time = performance.now() * 0.003;
  for (const f of world.food) {
    const [sx, sy] = toScreen(f.x, f.y, scale, headOffsetY, world.scrollY);
    if (sy < -50 || sy > worldHeight + 50) continue;
    let color = p.food;
    let radius = 14 * scale;
    const isGolden = f.kind === 'GOLDEN';
    if (isGolden) {
      color = p.golden;
      radius = 22 * scale;
    }
    if (isGolden) {
      const pulse = 0.5 + 0.5 * Math.sin(time * 2.5);
      const outerR = radius + 14 * scale + pulse * 6 * scale;
      const gradient = ctx.createRadialGradient(sx, sy, radius * 0.5, sx, sy, outerR);
      gradient.addColorStop(0, rgba(color, 0.9));
      gradient.addColorStop(0.5, rgba(color, 0.4));
      gradient.addColorStop(1, rgba(color, 0));
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(sx, sy, outerR, 0, TAU);
      ctx.fill();
      ctx.strokeStyle = rgba(color, 0.8);
      ctx.lineWidth = 3 * scale;
      ctx.beginPath();
      ctx.arc(sx, sy, radius + 6 * scale, 0, TAU);
      ctx.stroke();
      ctx.fillStyle = rgba(0xffffff, 0.95);
      ctx.beginPath();
      ctx.arc(sx, sy, radius, 0, TAU);
      ctx.fill();
      ctx.fillStyle = rgba(color, 1.0);
      ctx.beginPath();
      ctx.arc(sx, sy, radius * 0.7, 0, TAU);
      ctx.fill();
    } else {
      const haloColor = p.food;
      ctx.fillStyle = rgba(haloColor, 0.35);
      ctx.beginPath();
      ctx.arc(sx, sy, radius + 8 * scale, 0, TAU);
      ctx.fill();
      ctx.fillStyle = rgba(color, 0.95);
      ctx.beginPath();
      ctx.arc(sx, sy, radius, 0, TAU);
      ctx.fill();
      ctx.strokeStyle = rgba(p.text, 0.6);
      ctx.lineWidth = 2 * scale;
      ctx.stroke();
    }
  }
}

function drawSnake(
  ctx: CanvasRenderingContext2D,
  world: World,
  p: Palette,
  scale: number,
  headOffsetY: number,
  worldHeight: number,
): void {
  const body = world.snakeBody;
  if (body.length === 0) return;

  const isDashing = false;

  for (let i = body.length - 1; i >= 1; i--) {
    const [x, y] = body[i]!;
    const [sx, sy] = toScreen(x, y, scale, headOffsetY, world.scrollY);
    if (sy < -50 || sy > worldHeight + 50) continue;
    const t = i / body.length;
    const baseRadius = (1 - t * 0.25) * 18 * scale;

    if (isDashing) {
      ctx.shadowBlur = 15 * scale;
      ctx.shadowColor = rgba(p.accent, 0.8);
    }

    ctx.fillStyle = rgba(p.snakeBody, 0.85);
    ctx.beginPath();
    ctx.arc(sx, sy, baseRadius, 0, TAU);
    ctx.fill();
    ctx.strokeStyle = rgba(p.snakeHead, 0.7);
    ctx.lineWidth = 1.5 * scale;
    ctx.stroke();

    ctx.shadowBlur = 0;
    ctx.shadowColor = 'transparent';
  }

  const [hx, hy] = body[0]!;
  const [sx, sy] = toScreen(hx, hy, scale, headOffsetY, world.scrollY);

  if (isDashing) {
    ctx.shadowBlur = 25 * scale;
    ctx.shadowColor = rgba(p.accent, 0.9);
  }

  ctx.fillStyle = rgba(p.snakeHead);
  ctx.beginPath();
  ctx.arc(sx, sy, 20 * scale, 0, TAU);
  ctx.fill();

  ctx.shadowBlur = 0;
  ctx.shadowColor = 'transparent';

  ctx.fillStyle = '#000';
  ctx.beginPath();
  ctx.arc(sx - 5 * scale, sy - 3 * scale, 3 * scale, 0, TAU);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(sx + 5 * scale, sy - 3 * scale, 3 * scale, 0, TAU);
  ctx.fill();
  if (Math.abs(world.snakeVx) > 60) {
    const stripeX = sx + (world.snakeVx > 0 ? 16 * scale : -16 * scale);
    ctx.fillStyle = rgba(p.snakeStripe, 0.7);
    ctx.beginPath();
    ctx.arc(stripeX, sy + 6 * scale, 6 * scale, 0, TAU);
    ctx.fill();
  }

  const crownLevels: [number, string][] = [
    [10, '★'],
    [20, '★★'],
    [35, '★★★'],
    [50, '♔'],
  ];
  for (const [threshold, crown] of crownLevels) {
    if (world.snakeLength >= threshold) {
      ctx.fillStyle = rgba(0xffd700ff);
      ctx.font = `bold ${12 * scale}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText(crown, sx, sy - 24 * scale);
    }
  }
}

function drawAllParticles(
  ctx: CanvasRenderingContext2D,
  world: World,
  scale: number,
  headOffsetY: number,
  worldHeight: number,
): void {
  for (const particle of world.particles) {
    const sy = (world.scrollY - particle.y) * scale + headOffsetY;
    if (sy < -30 || sy > worldHeight + 30) continue;
    const cx = particle.x * scale;
    const lifeFrac = Math.max(0, Math.min(1, particle.life / particle.maxLife));
    const color = withAlpha(particle.color, lifeFrac);
    ctx.fillStyle = rgba(color, lifeFrac);
    ctx.beginPath();
    ctx.arc(cx, sy, particle.radius * scale * (0.5 + 0.5 * lifeFrac), 0, TAU);
    ctx.fill();
  }
}

function drawRushCalmOverlay(
  ctx: CanvasRenderingContext2D,
  world: World,
  p: Palette,
  w: number,
  h: number,
): void {
  if (world.rushSeconds > 0) {
    const a = Math.max(0, Math.min(1, world.rushSeconds / 1.5)) * 0.4;
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, rgba(p.danger, a));
    grad.addColorStop(0.5, rgba(p.danger, 0));
    grad.addColorStop(1, rgba(p.danger, a));
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
  }
  if (world.calmSeconds > 0) {
    const a = Math.max(0, Math.min(1, world.calmSeconds / 2.5)) * 0.32;
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, rgba(p.accent, a));
    grad.addColorStop(0.5, rgba(p.accent, 0));
    grad.addColorStop(1, rgba(p.accent, a));
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
  }
}

function drawBiomeBanner(
  ctx: CanvasRenderingContext2D,
  world: World,
  p: Palette,
  w: number,
  h: number,
): void {
  if (!world.biomeMessage || world.biomeMessageSeconds <= 0) return;
  const a = Math.max(0, Math.min(1, world.biomeMessageSeconds / 2.4)) * 0.9;
  ctx.fillStyle = rgba(p.hudBg, a * 0.8);
  ctx.fillRect(w * 0.1, h * 0.35, w * 0.8, 90);
  ctx.fillStyle = rgba(p.text, a);
  ctx.font = `bold 32px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(world.biomeMessage, w / 2, h * 0.35 + 45);
}

function drawAllPopups(
  ctx: CanvasRenderingContext2D,
  world: World,
  scale: number,
  headOffsetY: number,
  worldHeight: number,
): void {
  for (const popup of world.popups) {
    const sy = (world.scrollY - popup.y) * scale + headOffsetY;
    if (sy < -30 || sy > worldHeight + 30) continue;
    const lifeFrac = Math.max(0, Math.min(1, popup.life / popup.maxLife));
    const alpha = lifeFrac;
    const yOffset = (1 - lifeFrac) * 40 * scale;
    ctx.fillStyle = rgba(popup.color, alpha);
    ctx.font = `bold ${14 * scale}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(popup.text, popup.x * scale, sy - yOffset);
  }
}

function drawSpeedLines(
  ctx: CanvasRenderingContext2D,
  world: World,
  p: Palette,
  w: number,
  h: number,
): void {
  const intensity = world.rushSeconds > 0 ? 0.35 : 0;
  if (intensity <= 0) return;
  ctx.save();
  ctx.globalAlpha = intensity;
  ctx.strokeStyle = rgba(p.accent, 0.6);
  ctx.lineWidth = 2;
  const seed = world.runSeed;
  const count = 30;
  for (let i = 0; i < count; i++) {
    const x = ((seed * (i + 1) * 13.37) % 1) * w;
    const y = ((world.scrollY * 0.5 + i * 137) % (h + 200)) - 100;
    const len = 40 + ((seed * i) % 60);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x, y + len);
    ctx.stroke();
  }
  ctx.restore();
}

function drawInvincibilityAura(
  ctx: CanvasRenderingContext2D,
  world: World,
  p: Palette,
  scale: number,
  headOffsetY: number,
): void {
  if (world.invincibilitySeconds <= 0 || world.snakeBody.length === 0) return;
  const [hx, hy] = world.snakeBody[0]!;
  const [sx, sy] = toScreen(hx, hy, scale, headOffsetY, world.scrollY);
  const pulse = 1 + Math.sin(performance.now() / 100) * 0.1;
  ctx.fillStyle = rgba(p.shield, 0.25 * pulse);
  ctx.beginPath();
  ctx.arc(sx, sy, 50 * scale * pulse, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(p.shield, 0.6);
  ctx.lineWidth = 2 * scale;
  ctx.beginPath();
  ctx.arc(sx, sy, 44 * scale * pulse, 0, TAU);
  ctx.stroke();
}
